import { NotificationManager } from "react-notifications";
import { FetchApiError } from "../api/api";
import StructureAPI, {
  loadWebpageComponents,
} from "../store/reducers/builderReducers/structure/structureApi";
import TemplateAPI, {
  DEFAULT_NEW_HEADER_FOOTER_TEMPLATE_LANGUAGE_CODE,
} from "../store/reducers/builderReducers/templates/templateApi";
import { AllComponents } from "../components/_default/interfaces/base";
import store from "../store/store";
import { getLegacyWebpageIdForStructure } from "../store/reducers/builderReducers/pages/pageIdentity";
import type { Page } from "../store/reducers/builderReducers/pages/pagesTypes";
import type { Template } from "../store/reducers/builderReducers/templates/templateApi";
import { getTemplateSourceComponent } from "../store/reducers/builderReducers/templates/templateHelpers";
import { DEFAULT_NEW_COLUMN_CLASSES } from "./Helpers";
import type { Language } from "../store/reducers/builderReducers/languageSettings/languageSettingsApi";
import { languageCodeFromActiveLanguage } from "./languageCode";

function resolveLanguageCodeForNewHeaderFooterTemplate(
  activeLanguages: Language[],
  locale: string | undefined
): string {
  const loc = typeof locale === "string" ? locale.trim() : "";
  if (loc) {
    const row = activeLanguages.find(
      (x) => languageCodeFromActiveLanguage(x).toLowerCase() === loc.toLowerCase()
    );
    const code = row ? languageCodeFromActiveLanguage(row).trim() : "";
    if (code) return code;
  }
  return DEFAULT_NEW_HEADER_FOOTER_TEMPLATE_LANGUAGE_CODE;
}

function notifyTemplateCreateError(error: unknown) {
  const message =
    error instanceof FetchApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : "Could not create template.";
  NotificationManager.error(message, null, 5000);
}

function getActivePageNumericId(
  state: ReturnType<typeof store.getState>
): number | null {
  const list = state.builder.pages.list as Page[];
  return getLegacyWebpageIdForStructure(
    list,
    state.builder.pages.active as string | null
  );
}

function templatesFromBuilderState(templates: unknown): Template[] {
  if (
    templates &&
    typeof templates === "object" &&
    typeof (templates as { toArray?: () => Template[] }).toArray === "function"
  ) {
    return (templates as { toArray: () => Template[] }).toArray();
  }
  if (Array.isArray(templates)) {
    return templates as Template[];
  }
  return [];
}

function findTemplateRootSource(
  templates: unknown,
  ref: number | string,
  kind: "header" | "footer"
): AllComponents | null {
  const key = String(ref).trim();
  if (!key) {
    return null;
  }
  for (const t of templatesFromBuilderState(templates)) {
    const src = getTemplateSourceComponent(t);
    if (!src) {
      continue;
    }
    const use = (t.use ?? "").trim().toLowerCase();
    const metadataMatchesKind =
      (kind === "header" && use === "header") ||
      (kind === "footer" && use === "footer");
    if (src.type !== kind && !metadataMatchesKind) {
      continue;
    }
    if (src.uuid?.trim() === key) {
      return src;
    }
    if (t.rootComponentUuid?.trim() === key) {
      return src;
    }
    if (src.id != null && String(src.id) === key) {
      return src;
    }
  }
  return null;
}

/** Header/footer attach source from `templates` list. */
function resolveHeaderFooterSourceForAttach(
  templates: unknown,
  ref: number | string,
  kind: "header" | "footer"
): AllComponents | null {
  return findTemplateRootSource(templates, ref, kind);
}


export function addHeader(
  headerId?: number | null,
  headerName: string = "",
  locale?: string,
  copy?: boolean
) {
  const state = store.getState();
  const languages = state.builder.languageSettings;

  const activeUuid = state.builder.pages.active as string | null;
  const activeNumericId = getActivePageNumericId(state);
  const webpageRef =
    typeof activeUuid === "string" && activeUuid.trim() !== ""
      ? activeUuid
      : activeNumericId;
  const currentPage = state.builder.pages.list.find(
    (x: Page) => x.uuid === activeUuid
  )!;

  if (!webpageRef || !currentPage) {
    return false;
  }

  const cachedTemplateInfo = {
    name: headerName ? headerName : "New Header",
    languageCode: resolveLanguageCodeForNewHeaderFooterTemplate(
      languages.activeLanguages,
      locale
    ),
    use: "header",
  };

  if (headerId && !headerName) {
    const attachSource = resolveHeaderFooterSourceForAttach(
      state.builder.templates,
      headerId,
      "header"
    );
    if (attachSource) {
      StructureAPI.injectTemplateComponent(webpageRef, attachSource, {
        sort_order: -1,
        reuseSourceIdentity: true,
        layoutRootKind: "header",
      });
      return true;
    }
    StructureAPI.addExistingComponent(webpageRef, {
      id: headerId,
      parent_id: null,
      sort_order: -1,
    });

    return true;
  }

  if (
    true &&
    headerName &&
    headerName !== "" &&
    locale &&
    locale !== "" &&
    !copy
  ) {
    StructureAPI.addComponent(webpageRef, {
      type: "header",
      languageCode: currentPage.languageCode,
      sort_order: -1,
      attributes: [
        {
          type: "attributes",
          name: "components",
          value: {
            logo: { alignment: "left", type: "logo" },
            nav: { alignment: "center", display: "block" },
            cart: { alignment: "right", display: "block" },
            language: {
              display: "block",
              type: "image",
              color: "#fff",
              size: "small",
              dropdown: "none",
            },
          },
        },
        { name: "paddingTop", value: "10px", type: "styles" },
        { name: "paddingLeft", value: "20px", type: "styles" },
        { name: "paddingBottom", value: "10px", type: "styles" },
        { name: "paddingRight", value: "20px", type: "styles" },
      ],
      childElements: [
        {
          type: "section",
          sort_order: 1,
          attributes: [
            { name: "classes", value: "col col-3", type: "attributes" },
            { name: "paddingLeft", value: "0px", type: "styles" },
            { name: "paddingRight", value: "0px", type: "styles" },
            { name: "paddingTop", value: "10px", type: "styles" },
            { name: "paddingBottom", value: "10px", type: "styles" },
            { name: "marginTop", value: "0px", type: "styles" },
            { name: "marginBottom", value: "0px", type: "styles" },
            { name: "marginRight", value: "0px", type: "styles" },
            { name: "marginLeft", value: "0px", type: "styles" },
          ],
          childElements: [
            {
              type: "image",
              sort_order: 1,
              attributes: [
                {
                  name: "maintainAspectRatio",
                  value: true,
                  type: "attributes",
                },
                { name: "width", value: "100%", type: "styles" },
                { name: "paddingLeft", value: "0px", type: "styles" },
                { name: "paddingRight", value: "0px", type: "styles" },
                { name: "paddingTop", value: "0px", type: "styles" },
                { name: "paddingBottom", value: "0px", type: "styles" },
                { name: "maxWidth", value: "122px", type: "styles" },
                { name: "minWidth", value: "100px", type: "styles" },
                { name: "minHeight", value: "40px", type: "styles" },
              ],
            },
          ],
        },
        {
          type: "section",
          sort_order: 1,
          attributes: [
            { name: "classes", value: "col col-6", type: "attributes" },
            { name: "paddingLeft", value: "0px", type: "styles" },
            { name: "paddingRight", value: "0px", type: "styles" },
            { name: "paddingTop", value: "10px", type: "styles" },
            { name: "paddingBottom", value: "10px", type: "styles" },
            { name: "marginTop", value: "0px", type: "styles" },
            { name: "marginBottom", value: "0px", type: "styles" },
            { name: "marginRight", value: "0px", type: "styles" },
            { name: "marginLeft", value: "0px", type: "styles" },
          ],
          childElements: [
            {
              type: "navigation-container",
              sort_order: 1,
              attributes: [
                { name: "paddingLeft", value: "10px", type: "styles" },
                { name: "paddingRight", value: "10px", type: "styles" },
                { name: "paddingTop", value: "10px", type: "styles" },
                { name: "paddingBottom", value: "10px", type: "styles" },
              ],

              childElements: [
                {
                  type: "nav-item",
                  sort_order: 1,
                  attributes: [
                    {
                      type: "attributes",
                      name: "text",
                      value: "Avaleht",
                    },
                    {
                      type: "attributes",
                      name: "link",
                      value: { url: "", type: "None", target: "" },
                    },
                    {
                      type: "attributes",
                      name: "typography_id",
                      value: null,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          type: "section",
          sort_order: 1,
          attributes: [
            {
              name: "classes",
              value: "col col-3 text-end text-xs-end text-sm-end",
              type: "attributes",
            },
            { name: "paddingLeft", value: "0px", type: "styles" },
            { name: "paddingRight", value: "0px", type: "styles" },
            { name: "paddingTop", value: "10px", type: "styles" },
            { name: "paddingBottom", value: "10px", type: "styles" },
            { name: "marginTop", value: "0px", type: "styles" },
            { name: "marginBottom", value: "0px", type: "styles" },
            { name: "marginRight", value: "0px", type: "styles" },
            { name: "marginLeft", value: "0px", type: "styles" },
          ],
          childElements: [
            {
              type: "cart",
              sort_order: 1,
              attributes: [
                { name: "marginTop", value: "10px", type: "styles" },
              ],
            },
          ],
        },
      ],
    })
      .then((res: any) => {
        const addedRoot = res?.addedRoot ?? null;
        return TemplateAPI.createTemplate({
          name: cachedTemplateInfo.name,
          languageCode: cachedTemplateInfo.languageCode,
          use: cachedTemplateInfo.use,
          type: "header",
          visible: true,
          webpages: webpageRef != null ? [webpageRef] : [],
          component: addedRoot,
        }).then(() => {
          NotificationManager.success(
            "Template created successfully.",
            null,
            3000
          );
        });
      })
      .catch((error: unknown) => {
        notifyTemplateCreateError(error);
      });

    return true;
  }

  if (copy && locale && headerName && headerId) {
    StructureAPI.addExistingComponent(webpageRef, {
      id: headerId,
      parent_id: null,
      sort_order: -1,
    }).then(() => {
      loadWebpageComponents(webpageRef);
    });
  }


  return true;
}

export function addFooter(
  footerId?: number | null,
  footerName: string = "",
  locale?: string,
  copy?: boolean
) {
  const state = store.getState();
  const typographies = state.builder.typographies;
  const languages = state.builder.languageSettings;

  const activeUuid = state.builder.pages.active as string | null;
  const activeNumericId = getActivePageNumericId(state);
  const webpageRef =
    typeof activeUuid === "string" && activeUuid.trim() !== ""
      ? activeUuid
      : activeNumericId;
  const currentPage = state.builder.pages.list.find(
    (x: Page) => x.uuid === activeUuid
  )!;

  if (!webpageRef || !currentPage) {
    return false;
  }

  const cachedTemplateInfo = {
    name: footerName ? footerName : "New Footer",
    languageCode: resolveLanguageCodeForNewHeaderFooterTemplate(
      languages.activeLanguages,
      locale
    ),
    use: "footer",
  };

  if (footerId && !footerName) {
    const attachSource = resolveHeaderFooterSourceForAttach(
      state.builder.templates,
      footerId,
      "footer"
    );
    if (attachSource) {
      StructureAPI.injectTemplateComponent(webpageRef, attachSource, {
        sort_order: 999,
        reuseSourceIdentity: true,
        layoutRootKind: "footer",
      });
      return true;
    }
    StructureAPI.addExistingComponent(webpageRef, {
      id: footerId,
      parent_id: null,
      sort_order: 999,
    });

    return true;
  }

  if (
    true &&
    footerName &&
    footerName !== "" &&
    locale &&
    locale !== "" &&
    !copy
  ) {
    StructureAPI.addComponent(webpageRef, {
      type: "footer",
      sort_order: 99999,
      languageCode: currentPage.languageCode,
      attributes: [
        {
          type: "attributes",
          name: "classes",
          value: "builder-footer",
        },
        { name: "paddingTop", value: "10px", type: "styles" },
        { name: "paddingLeft", value: "10px", type: "styles" },
        { name: "paddingBottom", value: "10px", type: "styles" },
        { name: "paddingRight", value: "10px", type: "styles" },
      ],
      childElements: [
        {
          type: "section",
          sort_order: 1,
          attributes: [
            {
              type: "attributes",
              name: "classes",
              value: "row",
            },
            { name: "paddingTop", value: "10px", type: "styles" },
            { name: "paddingLeft", value: "10px", type: "styles" },
            { name: "paddingBottom", value: "10px", type: "styles" },
            { name: "paddingRight", value: "10px", type: "styles" },
          ],
          childElements: [
            {
              type: "section",
              sort_order: 1,
              attributes: [
                {
                  type: "attributes",
                  name: "classes",
                  value: DEFAULT_NEW_COLUMN_CLASSES,
                },
                { name: "paddingTop", value: "30px", type: "styles" },
                { name: "paddingLeft", value: "40px", type: "styles" },
                { name: "paddingBottom", value: "40px", type: "styles" },
                { name: "paddingRight", value: "30px", type: "styles" },
              ],
              childElements: [
                {
                  type: "text",
                  sort_order: 1,
                  attributes: [
                    {
                      type: "attributes",
                      name: "text",
                      value: "Footer text",
                    },
                    {
                      type: "attributes",
                      name: "typography_id",
                      value: typographies.find((x) => x.tag == "p")?.id ?? null,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    })
      .then((res: any) => {
        const addedRoot = res?.addedRoot ?? null;
        return TemplateAPI.createTemplate({
          name: cachedTemplateInfo.name,
          languageCode: cachedTemplateInfo.languageCode,
          use: cachedTemplateInfo.use,
          type: "footer",
          visible: true,
          webpages: webpageRef != null ? [webpageRef] : [],
          component: addedRoot,
        }).then(() => {
          NotificationManager.success(
            "Template created successfully.",
            null,
            3000
          );
        });
      })
      .catch((error: unknown) => {
        notifyTemplateCreateError(error);
      });

    return true;
  }

  if (copy && locale && footerName && footerId) {
    StructureAPI.addExistingComponent(webpageRef, {
      id: footerId,
      parent_id: null,
      sort_order: 999,
    }).then(() => {
      loadWebpageComponents(webpageRef);
    });
  }

  return true;
}

export function addCookies() {
  const state = store.getState();
  const activeUuid = state.builder.pages.active as string | null;
  if (!activeUuid) {
    return false;
  }
  return true;
}
